/* begin general import */
import { API_RESPONSIBILITY_PREFIX } from "config/api-consts";
import { RESPONSIBILITY_ROUTE } from "config/route-consts";
import { ResponsibilityFilter } from "models/Responsibility";
import { SiteFilter } from "models/Site";
import { StatusFilter } from "models/Status";
import React from "react";
import { useTranslation } from "react-i18next";
import { IdFilter, StringFilter } from "react3l-advanced-filters";
import { AdvanceIdFilter } from "react3l-ui-library";
import AdvanceStringFilter from "react3l-ui-library/build/components/AdvanceFilter/AdvanceStringFilter";
import Drawer, {
  DrawerProps,
} from "react3l-ui-library/build/components/Drawer/Drawer";
import { Reducer } from "redux";
import { responsibilityRepository } from "repositories/responsibility-repository";
import { authorizationService } from "services/common-services/authorization-service";
import {
  FilterAction,
  filterReducer,
  filterService,
} from "services/page-services/filter-service";
/* end individual import */

export interface ResponsibilityFilterProps extends DrawerProps {
  filter?: any;
  setVisible?: any;
  handleChangeAllFilter?: (data: any) => void;
}

function ResponsibilityAdvanceFilter(props: ResponsibilityFilterProps) {
  const [translate] = useTranslation();

  const { validAction } = authorizationService.useAuthorizedAction(
    RESPONSIBILITY_ROUTE.split("/")[RESPONSIBILITY_ROUTE.split("/").length - 1],
    API_RESPONSIBILITY_PREFIX
  );

  const { visible, filter, setVisible, handleChangeAllFilter, handleClose } =
    props;

  const [modelFilter, setModelFilter] = React.useReducer<
    Reducer<ResponsibilityFilter, FilterAction<ResponsibilityFilter>>
  >(filterReducer, filter);

  const { handleChangeInputFilter, handleChangeSelectFilter } =
    filterService.useFilter(filter, setModelFilter);

  const handleSaveModelFilter = React.useCallback(() => {
    handleChangeAllFilter(modelFilter);
    setVisible(false);
  }, [handleChangeAllFilter, modelFilter, setVisible]);

  const handleClearModelFilter = React.useCallback(() => {
    setModelFilter({
      type: 0,
      payload: new ResponsibilityFilter(),
    });
  }, [setModelFilter]);

  return (
    <Drawer
      visible={visible}
      handleSave={handleSaveModelFilter}
      handleCancel={handleClearModelFilter}
      handleClose={handleClose}
      visibleFooter={true}
      loading={false}
      size={"sm"}
      title={translate("general.drawer.filter")}
      titleButtonCancel={translate("general.actions.clear")}
      titleButtonApply={translate("general.actions.apply")}
    >
      <div className="d-flex m-t--sm">
        <AdvanceStringFilter
          label={translate("responsibilities.code")}
          showCount={true}
          maxLength={100}
          value={modelFilter["code"]["contain"]}
          onChange={handleChangeInputFilter({
            fieldName: "code",
            fieldType: "contain",
            classFilter: StringFilter,
          })}
          placeHolder={translate("responsibilities.placeholder.code")}
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceStringFilter
          label={translate("responsibilities.name")}
          showCount={true}
          maxLength={100}
          value={modelFilter["name"]["contain"]}
          onChange={handleChangeInputFilter({
            fieldName: "name",
            fieldType: "contain",
            classFilter: StringFilter,
          })}
          placeHolder={translate("responsibilities.placeholder.name")}
        />
      </div>
      {validAction("filterListStatus") && (
        <div className="d-flex m-t--sm">
          <AdvanceIdFilter
            value={modelFilter["statusValue"]}
            placeHolder={translate("responsibilities.placeholder.status")}
            classFilter={StatusFilter}
            onChange={handleChangeSelectFilter({
              fieldName: "status",
              fieldType: "equal",
              classFilter: IdFilter,
            })}
            getList={responsibilityRepository.filterListStatus}
            label={translate("responsibilities.status")}
          />
        </div>
      )}
      {validAction("filterListSite") && (
        <div className="d-flex m-t--sm">
          <AdvanceIdFilter
            value={modelFilter["siteValue"]}
            placeHolder={translate("responsibilities.placeholder.site")}
            classFilter={SiteFilter}
            onChange={handleChangeSelectFilter({
              fieldName: "site",
              fieldType: "equal",
              classFilter: IdFilter,
            })}
            getList={responsibilityRepository.filterListSite}
            label={translate("responsibilities.site")}
          />
        </div>
      )}
    </Drawer>
  );
}

export default ResponsibilityAdvanceFilter;
